import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useLang } from '../lib/lang';

// DML Brand tokens
const DML = {
  gold: '#C9975B',
  goldLight: '#E3CEB1',
  goldLighter: '#F5EDE0',
  goldDark: '#9A6630',
  cream: '#FAF8F2',
  creamDark: '#F0EBE0',
  navy: '#161616',
  navyMid: '#2a2a2a',
  white: '#FFFFFF',
  fontHeading: "'Barlow', sans-serif",
  fontBody: "'Barlow', 'avenir-lt-w01_35-light1475496', sans-serif",
};

const arFont = "'Noto Kufi Arabic', 'IBM Plex Sans Arabic', serif";

const STATUS_COLORS = {
  // English (legacy)
  active:  { bg: '#f0f8f4', color: '#1a6b3c', border: '#b8dfc9' },
  pending: { bg: '#fdf6e8', color: '#92651e', border: '#e8d199' },
  closed:  { bg: '#f5f5f5', color: '#666',    border: '#ddd' },
  // Arabic
  'منظورة':           { bg: '#f0f8f4', color: '#1a6b3c', border: '#b8dfc9' },
  'قيد التسجيل':      { bg: '#fdf6e8', color: '#92651e', border: '#e8d199' },
  'فصل':              { bg: '#f5f5f5', color: '#555',    border: '#ddd' },
  'فصل - رفض الطلب': { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
  'فصل - سلبي':       { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
};

const DOC_TAG_COLORS = {
  pleading: { bg: '#f5ede0', color: DML.goldDark },
  record:   { bg: '#e9f0f8', color: '#1a4a7a' },
  evidence: { bg: '#f0f8f4', color: '#1a6b3c' },
  order:    { bg: '#fdf6e8', color: '#92651e' },
  other:    { bg: '#f5f5f5', color: '#666' },
};

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}

function formatDate(dateStr, lang) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString(lang === 'ar' ? 'ar-JO' : 'en-GB', {
    day: 'numeric', month: 'long', year: 'numeric'
  });
}

function formatCal(dateStr, lang) {
  if (!dateStr) return { month: '', day: '' };
  const d = new Date(dateStr);
  return {
    month: d.toLocaleDateString(lang === 'ar' ? 'ar-JO' : 'en-GB', { month: 'short' }).toUpperCase(),
    day: d.getDate()
  };
}

export default function CaseView() {
  const { t, lang, setLang } = useLang();
  const isAr = lang === 'ar';
  const ff = isAr ? arFont : DML.fontBody;

  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [hearings, setHearings] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdMsg, setPwdMsg] = useState('');
  const [pwdErr, setPwdErr] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);

  useEffect(() => {
    async function loadUser() {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      if (!user) { setLoading(false); return; }

      const { data: userCases, error: casesError } = await supabase
        .from('cases').select('*').eq('client_email', user.email).order('created_at', { ascending: false });

      if (casesError) {
        setError(t.errorFetchingCases || 'Error loading cases');
      } else if (!userCases || userCases.length === 0) {
        setError(t.noCase);
      } else {
        setCases(userCases);
        // If only one case, select it by default
        if (userCases.length === 1) {
          handleSelectCase(userCases[0]);
        }
      }
      setLoading(false);
    }
    loadUser();
  }, []);

  const handleSelectCase = async (caseObj) => {
    setLoading(true);
    setSelectedCase(caseObj);

    const { data: h } = await supabase
      .from('hearings').select('*').eq('case_id', caseObj.id).order('hearing_date', { ascending: true });
    setHearings(h || []);

    const { data: docs } = await supabase
      .from('documents').select('*').eq('case_id', caseObj.id).order('uploaded_at', { ascending: false });
    setDocuments(docs || []);
    
    setLoading(false);
  };

  const handleBackToList = () => {
    setSelectedCase(null);
    setHearings([]);
    setDocuments([]);
  };

  const handleSignOut = () => supabase.auth.signOut();

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwdMsg('');
    setPwdErr('');
    if (newPwd.length < 8) { setPwdErr(t.passwordTooShort); return; }
    if (newPwd !== confirmPwd) { setPwdErr(t.passwordsDontMatch); return; }
    setPwdLoading(true);
    // Re-authenticate first, then update password
    const { error: signInErr } = await supabase.auth.signInWithPassword({ email: user.email, password: currentPwd });
    if (signInErr) { setPwdErr(signInErr.message); setPwdLoading(false); return; }
    const { error: updateErr } = await supabase.auth.updateUser({ password: newPwd });
    if (updateErr) { setPwdErr(updateErr.message); } else {
      setPwdMsg(t.passwordChanged);
      setTimeout(() => supabase.auth.signOut(), 2000);
    }
    setPwdLoading(false);
  };

  const handleDownload = async (doc) => {
    const { data } = await supabase.storage.from('case-documents').createSignedUrl(doc.storage_path, 300);
    if (data?.signedUrl) window.open(data.signedUrl, '_blank');
  };

  if (loading && !selectedCase && cases.length === 0) return (
    <div style={{ minHeight: '100vh', background: DML.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: ff, color: DML.goldDark, fontSize: '14px', letterSpacing: '0.5px' }}>
      {t.loading}
    </div>
  );

  if (error) return (
    <div dir={t.dir} style={{ minHeight: '100vh', background: DML.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: ff }}>
      <div style={{ textAlign: 'center', color: '#b91c1c', fontSize: '14px', maxWidth: '320px', padding: '2rem' }}>
        {error}<br /><br />
        <button onClick={handleSignOut} style={{ fontSize: '12px', color: DML.goldDark, background: 'none', border: `1px solid ${DML.goldLight}`, borderRadius: '4px', padding: '8px 16px', cursor: 'pointer', fontFamily: ff }}>
          {t.signOut}
        </button>
      </div>
    </div>
  );

  const status = selectedCase?.status || 'active';
  const statusColors = STATUS_COLORS[status] || STATUS_COLORS.closed;
  const nextHearing = hearings.find(h => h.status === 'upcoming');
  const days = nextHearing ? daysUntil(nextHearing.hearing_date) : null;
  const cal = nextHearing ? formatCal(nextHearing.hearing_date, lang) : null;

  return (
    <div dir={t.dir} style={{ background: DML.cream, minHeight: '100vh', fontFamily: ff }}>

      {/* Gold top bar */}
      <div style={{ height: '3px', background: `linear-gradient(90deg, transparent, ${DML.gold}, transparent)` }} />

      {/* Header */}
      <div style={{ background: DML.navy, padding: '1rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img src="/dml-logo.png" alt="Dar Al Masri Law" style={{ height: '64px', objectFit: 'contain' }} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {selectedCase && (
            <span style={{ background: 'rgba(201,151,91,0.12)', border: `1px solid ${DML.goldDark}`, borderRadius: '2px', padding: '4px 12px', fontSize: '11px', color: DML.gold, letterSpacing: '0.5px' }}>
              {selectedCase.case_number}
            </span>
          )}
          <button onClick={() => setLang(isAr ? 'en' : 'ar')} style={{
            background: 'transparent', border: `1px solid ${DML.goldDark}`, borderRadius: '2px',
            color: DML.goldLight, fontSize: '11px', padding: '4px 12px', cursor: 'pointer',
            fontFamily: ff, letterSpacing: '0.5px'
          }}>
            {isAr ? 'English' : 'العربية'}
          </button>
        </div>
      </div>

      {/* Login bar */}
      <div style={{ background: DML.creamDark, borderBottom: `1px solid ${DML.goldLighter}`, padding: '0.5rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#888' }}>
          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#2d7a4f' }} />
          {t.loggedInAs}: <strong style={{ color: DML.navy }}>{user?.email}</strong>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={() => { setShowPwdModal(true); setPwdMsg(''); setPwdErr(''); setCurrentPwd(''); setNewPwd(''); setConfirmPwd(''); }} style={{ fontSize: '11px', color: DML.goldDark, background: 'none', border: 'none', cursor: 'pointer', fontFamily: ff, letterSpacing: '0.5px' }}>
            {t.changePassword}
          </button>
          <button onClick={handleSignOut} style={{ fontSize: '11px', color: DML.goldDark, background: 'none', border: 'none', cursor: 'pointer', fontFamily: ff, letterSpacing: '0.5px' }}>
            {t.signOut}
          </button>
        </div>
      </div>

      <div style={{ maxWidth: '860px', margin: '2rem auto', padding: '0 1rem 3rem' }}>
        {!selectedCase ? (
          /* Case List View */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h2 style={{ fontFamily: DML.fontHeading, fontSize: '18px', fontWeight: '500', color: DML.navy, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
              {isAr ? 'قضاياك' : 'Your Cases'}
            </h2>
            {cases.map(c => {
              const sc = STATUS_COLORS[c.status] || STATUS_COLORS.active;
              return (
                <div key={c.id} 
                  onClick={() => handleSelectCase(c)}
                  style={{
                    background: DML.white,
                    border: `1px solid ${DML.goldLighter}`,
                    borderRadius: '2px',
                    padding: '1.5rem 2rem',
                    cursor: 'pointer',
                    boxShadow: '0 2px 12px rgba(201,151,91,0.04)',
                    transition: 'transform 0.15s, box-shadow 0.15s',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(201,151,91,0.08)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = '0 2px 12px rgba(201,151,91,0.04)';
                  }}
                >
                  <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: '4px', background: DML.gold }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                      <div style={{ fontSize: '10px', textTransform: 'uppercase', color: DML.gold, letterSpacing: '1.5px', marginBottom: '4px', fontWeight: '600' }}>
                        {c.case_number}
                      </div>
                      <h3 style={{ fontSize: '17px', color: DML.navy, marginBottom: '4px', fontWeight: '500' }}>{c.title}</h3>
                      <p style={{ fontSize: '12px', color: '#999' }}>{c.court_name}</p>
                    </div>
                    <div style={{
                      padding: '4px 10px', borderRadius: '2px', fontSize: '10px', fontWeight: '600',
                      letterSpacing: '0.5px', background: sc.bg, color: sc.color,
                      border: `1px solid ${sc.border}`, textTransform: 'uppercase'
                    }}>
                      {t[c.status] || c.status}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Single Case View */
          <div style={{ background: DML.white, border: `1px solid ${DML.goldLighter}`, borderRadius: '2px', overflow: 'hidden', boxShadow: '0 2px 24px rgba(201,151,91,0.06)' }}>
            
            {/* Nav / Back */}
            {cases.length > 1 && (
              <div style={{ padding: '1rem 2rem 0' }}>
                <button onClick={handleBackToList} style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  display: 'flex', alignItems: 'center', gap: '6px', color: DML.gold,
                  fontSize: '12px', fontWeight: '500', fontFamily: ff
                }}>
                  {isAr ? '← العودة للقائمة' : '← Back to List'}
                </button>
              </div>
            )}

            {/* Gold accent top */}
            <div style={{ height: '2px', background: DML.gold, marginTop: cases.length > 1 ? '1rem' : 0 }} />

            {/* Case hero */}
            <div style={{ padding: '2rem 2rem 1.5rem', borderBottom: `1px solid ${DML.goldLighter}` }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                <div>
                  <h1 style={{ fontFamily: DML.fontHeading, fontSize: '20px', fontWeight: '400', color: DML.navy, marginBottom: '6px', letterSpacing: '0.3px' }}>
                    {selectedCase.title}
                  </h1>
                  <p style={{ fontSize: '13px', color: '#999', letterSpacing: '0.3px' }}>
                    {selectedCase.court_name}
                    {selectedCase.judge_name ? ` · ${isAr ? 'القاضي' : 'Judge'} ${selectedCase.judge_name}` : ''}
                  </p>
                </div>
                <div style={{
                  padding: '5px 14px', borderRadius: '2px', fontSize: '11px', fontWeight: '500',
                  letterSpacing: '0.5px', background: statusColors.bg, color: statusColors.color,
                  border: `1px solid ${statusColors.border}`, whiteSpace: 'nowrap'
                }}>
                  {t[status]}
                </div>
              </div>

              {/* Info grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '1.5rem' }}>
                {[
                  { label: t.caseType, value: selectedCase.case_type, sub: selectedCase.case_subtype },
                  { label: t.caseNumber, value: selectedCase.case_number, sub: selectedCase.filed_date ? `${t.filed}: ${formatDate(selectedCase.filed_date, lang)}` : '' },
                  { label: t.court, value: selectedCase.court_name, sub: selectedCase.court_room },
                ].map((item, i) => (
                  <div key={i} style={{ background: DML.cream, border: `1px solid ${DML.goldLighter}`, borderRadius: '2px', padding: '1rem' }}>
                    <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: DML.gold, marginBottom: '6px', fontWeight: '500' }}>{item.label}</div>
                    <div style={{ fontSize: '14px', color: DML.navy, fontWeight: '400' }}>{item.value}</div>
                    {item.sub && <div style={{ fontSize: '11px', color: '#aaa', marginTop: '3px' }}>{item.sub}</div>}
                  </div>
                ))}
              </div>

              {/* Latest Update */}
              {selectedCase.latest_update && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ fontSize: '10px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1.5px', color: DML.gold, marginBottom: '8px' }}>
                    {isAr ? 'آخر تحديث' : 'Latest Update'}
                  </div>
                  <div style={{
                    background: '#fdf6e8',
                    border: `1px solid #e8d199`,
                    borderRadius: '2px',
                    borderInlineStart: `3px solid ${DML.gold}`,
                    padding: '0.875rem 1.125rem',
                    fontSize: '14px',
                    color: DML.navy,
                    lineHeight: '1.6',
                    fontFamily: ff,
                  }}>
                    {selectedCase.latest_update}
                  </div>
                </div>
              )}

              {/* Next hearing */}
              {loading ? (
                <div style={{ textAlign: 'center', padding: '1rem', color: DML.goldLight, fontSize: '12px' }}>{t.loading}...</div>
              ) : nextHearing && (
                <div style={{ background: DML.navy, borderRadius: '2px', padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap', border: `1px solid ${DML.goldDark}` }}>
                  <div style={{ textAlign: 'center', background: DML.cream, borderRadius: '2px', padding: '8px 14px', minWidth: '58px' }}>
                    <div style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '1px', color: DML.goldDark, fontWeight: '500' }}>{cal.month}</div>
                    <div style={{ fontSize: '26px', fontWeight: '300', color: DML.navy, lineHeight: 1, fontFamily: DML.fontHeading }}>{cal.day}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: '500', color: DML.gold, marginBottom: '4px', letterSpacing: '0.3px' }}>{t.nextHearing}</div>
                    <div style={{ fontSize: '12px', color: 'rgba(201,151,91,0.6)' }}>
                      {formatDate(nextHearing.hearing_date, lang)}
                      {nextHearing.time ? ` · ${nextHearing.time}` : ''}
                      {nextHearing.location ? ` · ${nextHearing.location}` : ''}
                    </div>
                  </div>
                  {days !== null && days >= 0 && (
                    <span style={{ background: 'rgba(201,151,91,0.1)', border: `1px solid ${DML.goldDark}`, borderRadius: '2px', padding: '3px 12px', fontSize: '11px', color: DML.gold, letterSpacing: '0.5px' }}>
                      {days} {t.daysAway}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Hearing timeline */}
            <div style={{ padding: '1.75rem 2rem', borderBottom: `1px solid ${DML.goldLighter}` }}>
              <div style={{ fontSize: '10px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1.5px', color: DML.gold, marginBottom: '1.25rem' }}>{t.hearingHistory}</div>
              {!loading && hearings.length === 0 && <p style={{ fontSize: '13px', color: '#ccc' }}>—</p>}
              {hearings.map((h, i) => (
                <div key={h.id} style={{ display: 'flex', gap: '1rem', paddingBottom: i < hearings.length - 1 ? '1.25rem' : 0, position: 'relative' }}>
                  {i < hearings.length - 1 && (
                    <div style={{ position: 'absolute', [isAr ? 'right' : 'left']: '15px', top: '32px', bottom: 0, width: '1px', background: DML.goldLighter }} />
                  )}
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', flexShrink: 0, fontSize: '12px',
                    ...(h.status === 'upcoming'
                      ? { background: DML.cream, color: DML.gold, border: `1px solid ${DML.goldLight}` }
                      : { background: DML.goldLighter, color: DML.goldDark, border: `1px solid ${DML.goldLight}` })
                  }}>
                    {h.status === 'upcoming' ? '→' : '✓'}
                  </div>
                  <div style={{ flex: 1, paddingTop: '4px' }}>
                    <div style={{ fontSize: '11px', color: '#bbb', marginBottom: '2px', letterSpacing: '0.3px' }}>
                      {formatDate(h.hearing_date, lang)}
                      {h.status === 'upcoming' && <span style={{ marginInlineStart: '8px', color: DML.gold, fontWeight: '500' }}>{t.upcoming}</span>}
                    </div>
                    <div style={{ fontSize: '14px', color: DML.navy, marginBottom: '3px' }}>{h.title}</div>
                    {h.outcome && <div style={{ fontSize: '12px', color: '#999' }}>{h.outcome}</div>}
                  </div>
                </div>
              ))}
            </div>

            {/* Documents */}
            <div style={{ padding: '1.75rem 2rem' }}>
              <div style={{ fontSize: '10px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1.5px', color: DML.gold, marginBottom: '1.25rem' }}>
                {t.documents} {!loading && documents.length > 0 && `(${documents.length})`}
              </div>
              {!loading && documents.length === 0 && <p style={{ fontSize: '13px', color: '#ccc' }}>—</p>}
              {documents.length > 0 && (
                <div style={{ border: `1px solid ${DML.goldLighter}`, borderRadius: '2px', overflow: 'hidden' }}>
                  {documents.map((doc, i) => {
                    const tagColors = DOC_TAG_COLORS[doc.doc_type] || DOC_TAG_COLORS.other;
                    const sizeMB = doc.file_size_kb ? (doc.file_size_kb / 1024).toFixed(1) : null;
                    return (
                      <div key={doc.id}
                        style={{ padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '1rem', borderBottom: i < documents.length - 1 ? `1px solid ${DML.goldLighter}` : 'none', cursor: 'pointer', transition: 'background 0.12s' }}
                        onMouseEnter={e => e.currentTarget.style.background = DML.cream}
                        onMouseLeave={e => e.currentTarget.style.background = DML.white}
                        onClick={() => handleDownload(doc)}
                      >
                        <div style={{ width: '32px', height: '32px', borderRadius: '2px', background: DML.goldLighter, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '14px' }}>
                          📄
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '13px', color: DML.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.name}</div>
                          <div style={{ fontSize: '11px', color: '#bbb', marginTop: '2px' }}>
                            {t.uploaded} {formatDate(doc.uploaded_at, lang)}{sizeMB && ` · ${sizeMB} ${t.mb}`}
                          </div>
                        </div>
                        <span style={{ fontSize: '10px', padding: '3px 10px', borderRadius: '2px', background: tagColors.bg, color: tagColors.color, whiteSpace: 'nowrap', flexShrink: 0, letterSpacing: '0.5px' }}>
                          {t[doc.doc_type] || t.other}
                        </span>
                        <button
                          onClick={e => { e.stopPropagation(); handleDownload(doc); }}
                          style={{ fontSize: '11px', color: DML.goldDark, border: `1px solid ${DML.goldLight}`, borderRadius: '2px', padding: '4px 10px', background: 'white', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: ff, letterSpacing: '0.3px' }}
                        >
                          {t.download}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Footer */}
              <div style={{ textAlign: 'center', fontSize: '11px', color: DML.goldLight, marginTop: '2rem', paddingTop: '1.25rem', borderTop: `1px solid ${DML.goldLighter}`, letterSpacing: '0.3px' }}>
                {selectedCase.last_updated && `${t.lastUpdated} ${selectedCase.firm_name || 'Dar Al Masri Law'} · ${formatDate(selectedCase.last_updated, lang)}`}
                <br /><span style={{ color: '#ccc' }}>{t.contactLawyer}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom gold line */}
      <div style={{ height: '3px', background: `linear-gradient(90deg, transparent, ${DML.gold}, transparent)` }} />

      {/* Password Change Modal */}
      {showPwdModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(22,22,22,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, fontFamily: ff }} onClick={() => setShowPwdModal(false)}>
          <div style={{ background: DML.white, borderRadius: '2px', border: `1px solid ${DML.goldLight}`, padding: '2rem', width: '100%', maxWidth: '400px', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontFamily: DML.fontHeading, fontSize: '16px', fontWeight: '500', color: DML.navy, marginBottom: '1.5rem', letterSpacing: '0.5px' }}>
              {t.changePassword}
            </h3>
            {pwdMsg && (
              <div style={{ background: '#f0f8f4', border: '1px solid #b8dfc9', borderRadius: '4px', padding: '10px 14px', marginBottom: '1rem', fontSize: '13px', color: '#1a6b3c' }}>{pwdMsg}</div>
            )}
            {pwdErr && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', padding: '10px 14px', marginBottom: '1rem', fontSize: '13px', color: '#b91c1c' }}>{pwdErr}</div>
            )}
            <form onSubmit={handleChangePassword}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ fontSize: '11px', fontWeight: '500', color: '#888', display: 'block', marginBottom: '6px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{t.currentPassword}</label>
                <input type="password" required value={currentPwd} onChange={e => setCurrentPwd(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: `1px solid ${DML.goldLight}`, borderRadius: '2px', outline: 'none', fontFamily: ff, boxSizing: 'border-box', background: DML.cream, color: DML.navy }}
                  onFocus={e => e.target.style.borderColor = DML.gold} onBlur={e => e.target.style.borderColor = DML.goldLight} />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ fontSize: '11px', fontWeight: '500', color: '#888', display: 'block', marginBottom: '6px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{t.newPassword}</label>
                <input type="password" required value={newPwd} onChange={e => setNewPwd(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: `1px solid ${DML.goldLight}`, borderRadius: '2px', outline: 'none', fontFamily: ff, boxSizing: 'border-box', background: DML.cream, color: DML.navy }}
                  onFocus={e => e.target.style.borderColor = DML.gold} onBlur={e => e.target.style.borderColor = DML.goldLight} />
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ fontSize: '11px', fontWeight: '500', color: '#888', display: 'block', marginBottom: '6px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{t.confirmPassword}</label>
                <input type="password" required value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: `1px solid ${DML.goldLight}`, borderRadius: '2px', outline: 'none', fontFamily: ff, boxSizing: 'border-box', background: DML.cream, color: DML.navy }}
                  onFocus={e => e.target.style.borderColor = DML.gold} onBlur={e => e.target.style.borderColor = DML.goldLight} />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowPwdModal(false)} style={{ fontSize: '12px', color: '#888', background: 'none', border: `1px solid #ddd`, borderRadius: '2px', padding: '8px 20px', cursor: 'pointer', fontFamily: ff }}>
                  {t.cancel}
                </button>
                <button type="submit" disabled={pwdLoading} style={{ fontSize: '12px', fontWeight: '500', color: DML.white, background: DML.navy, border: 'none', borderRadius: '2px', padding: '8px 24px', cursor: pwdLoading ? 'not-allowed' : 'pointer', opacity: pwdLoading ? 0.7 : 1, fontFamily: ff, letterSpacing: '0.5px' }}>
                  {pwdLoading ? t.changingPassword : t.save}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
